import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTenantSettingsService } from "../../api/tenantSettingsService";
import { useSnackbar } from "../../contexts/snackbar-context";
import { TenantSettings, UpdateTenantSettingsRequest } from "../../types/tenantSettings";
import {
  tenantManagementPaperSx,
  tenantManagementPrimaryButtonSx,
} from "./tenantManagementStyles";

const defaultSettings: UpdateTenantSettingsRequest = {
  showOwner: true,
  showOperator: true,
  showOwnershipStatus: true,
};

type DisplayPreferencesTabProps = {
  active: boolean;
};

export const DisplayPreferencesTab: React.FC<DisplayPreferencesTabProps> = ({
  active,
}) => {
  const { showSuccess, showError } = useSnackbar();
  const { getTenantSettings, updateTenantSettings } = useTenantSettingsService();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<UpdateTenantSettingsRequest>(defaultSettings);
  const [saved, setSaved] = useState<UpdateTenantSettingsRequest>(defaultSettings);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data: TenantSettings = await getTenantSettings();
      const next: UpdateTenantSettingsRequest = {
        showOwner: data.showOwner,
        showOperator: data.showOperator,
        showOwnershipStatus: data.showOwnershipStatus,
      };
      setForm(next);
      setSaved(next);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load display preferences.";
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [getTenantSettings, showError]);

  useEffect(() => {
    if (active) {
      void loadSettings();
    }
  }, [active, loadSettings]);

  const isDirty =
    form.showOperator !== saved.showOperator ||
    form.showOwnershipStatus !== saved.showOwnershipStatus;

  const handleToggle =
    (key: keyof UpdateTenantSettingsRequest) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForm((prev) => ({ ...prev, [key]: checked }));
    };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateTenantSettings(form);
      const next: UpdateTenantSettingsRequest = {
        showOwner: updated.showOwner,
        showOperator: updated.showOperator,
        showOwnershipStatus: updated.showOwnershipStatus,
      };
      setForm(next);
      setSaved(next);
      showSuccess("Display preferences saved");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save display preferences.";
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "hsl(19 101% 51% / 1)",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "hsl(19 101% 51% / 0.5)",
    },
  };

  return (
    <Paper sx={tenantManagementPaperSx}>
      <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)", mb: 1 }}>
        Display Preferences
      </Typography>
      <Typography sx={{ color: "#999", mb: 3 }}>
        Control which ownership fields appear on asset and document views for
        this tenant.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <FormControlLabel
            sx={{ color: "#fff", ml: 0 }}
            control={
              <Switch
                checked={form.showOwnershipStatus}
                onChange={handleToggle("showOwnershipStatus")}
                sx={switchSx}
              />
            }
            label="Show ownership status"
          />
          <FormControlLabel
            sx={{ color: "#fff", ml: 0 }}
            control={
              <Switch
                checked={form.showOperator}
                onChange={handleToggle("showOperator")}
                sx={switchSx}
              />
            }
            label="Show current operator"
          />

          <Box sx={{ pt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isDirty}
              sx={tenantManagementPrimaryButtonSx}
            >
              {saving ? "Saving..." : "Save Preferences"}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};
